// lib/whatsapp.js - WhatsApp Business API Integration
export class WhatsAppService {
  constructor() {
    // Environment variables
    this.accessToken = process.env.REACT_APP_WHATSAPP_ACCESS_TOKEN;
    this.phoneNumberId = process.env.REACT_APP_WHATSAPP_PHONE_NUMBER_ID;
    this.baseUrl = 'https://graph.facebook.com/v22.0';
    this.webhookVerifyToken = process.env.REACT_APP_WHATSAPP_WEBHOOK_VERIFY_TOKEN;

    // Validate required environment variables
    this.validateConfiguration();
  }

  validateConfiguration() {
    const requiredVars = ['accessToken', 'phoneNumberId'];
    const missing = requiredVars.filter(key => !this[key]);

    if (missing.length > 0) {
      throw new Error(`WhatsApp configuration incomplete. Missing: ${missing.join(', ')}`);
    }
  }


  isTokenExpiredError(error) {
    const errorMessage = error.message || error.toString();
    return errorMessage.includes('Session has expired') ||
      errorMessage.includes('access token') ||
      errorMessage.includes('Token validation failed');
  }


  handleTokenExpiration(error) {
    return {
      success: false,
      error: 'WhatsApp access token expired. Please contact administrator.',
      tokenExpired: true
    };
  }

  // Check message status by message ID
  async checkMessageStatus(messageId) {
    try {
      if (!this.accessToken || !messageId) {
        throw new Error('Missing access token or message ID');
      }

      const response = await fetch(`${this.baseUrl}/${messageId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          messageId: messageId,
          status: result.status,
          timestamp: result.timestamp,
          recipientId: result.recipient_id
        };
      } else {
        return {
          success: false,
          error: result.error?.message || `HTTP ${response.status}`,
          httpStatus: response.status
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to check message status',
        networkError: true
      };
    }
  }

  // Validate token by checking account info
  async validateToken() {
    try {
      if (!this.accessToken || !this.phoneNumberId) {
        throw new Error('Missing access token or phone number ID');
      }

      const response = await fetch(`${this.baseUrl}/${this.phoneNumberId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`
        }
      });

      const result = await response.json();

      if (response.ok) {
        return {
          success: true,
          phoneNumber: result.display_phone_number,
          verifiedName: result.verified_name,
          qualityRating: result.quality_rating,
          status: result.status,
          rawResponse: result
        };
      } else {
        return {
          success: false,
          error: result.error?.message || `HTTP ${response.status}`,
          httpStatus: response.status
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to validate token',
        networkError: true
      };
    }
  }

  // Format phone number for WhatsApp API (New Zealand focused)
  formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return null;

    // Remove all non-numeric characters
    let cleaned = phoneNumber.toString().replace(/\D/g, '');

    // Handle different NZ phone number formats
    if (cleaned.startsWith('64')) {
      // Already has country code
      return cleaned;
    } else if (cleaned.startsWith('0')) {
      // Convert 0XXXXXXXXX to 64XXXXXXXXX
      const formatted = '64' + cleaned.substring(1);
      return formatted;
    } else if (cleaned.length === 9 || cleaned.length === 10) {
      // Add country code to local numbers
      const formatted = '64' + cleaned;
      return formatted;
    } else if (cleaned.length >= 10) {
      // Use as international format
      return cleaned;
    }

    // If format not recognized, return as-is
    return cleaned;
  }

  // Send template message
  async sendTemplate(toNumber, templateName = 'hello_world', languageCode = 'en_US', templateParams = []) {
    try {
      const formattedNumber = this.formatPhoneNumber(toNumber);
      if (!formattedNumber) {
        throw new Error('Invalid phone number format');
      }

      // Marketing templates that may have 24-hour window restrictions
      const marketingTemplates = ['special_event_invite', 'new_class_schedule', 'general_announcement'];

      if (marketingTemplates.includes(templateName)) {
        // Marketing template - may require 24-hour conversation window
      }

      const payload = {
        messaging_product: 'whatsapp',
        to: formattedNumber,
        type: 'template',
        template: {
          name: templateName,
          language: {
            code: languageCode
          }
        }
      };

      // Add template parameters if provided
      if (templateParams && templateParams.length > 0) {
        payload.template.components = [{
          type: 'body',
          parameters: templateParams
        }];
      }

      const response = await fetch(`${this.baseUrl}/${this.phoneNumberId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.messages && result.messages.length > 0) {
        return {
          success: true,
          messageId: result.messages[0].id,
          status: result.messages[0].message_status,
          recipientId: result.contacts[0].wa_id,
          templateName: templateName,
          method: 'template'
        };
      } else {
        throw new Error(result.error?.message || `Template send failed: ${response.status}`);
      }
    } catch (error) {
      console.error('WhatsApp template send error:', error);

      if (this.isTokenExpiredError(error)) {
        return this.handleTokenExpiration(error);
      }

      return {
        success: false,
        error: error.message || 'Template send failed',
        templateName: templateName
      };
    }
  }

  // Send text message with automatic template fallback
  async sendTextMessage(toNumber, message) {
    try {
      const formattedNumber = this.formatPhoneNumber(toNumber);
      if (!formattedNumber) {
        throw new Error('Invalid phone number format');
      }

      const payload = {
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to: formattedNumber,
        type: 'text',
        text: {
          body: message
        }
      };

      const response = await fetch(`${this.baseUrl}/${this.phoneNumberId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.messages && result.messages.length > 0) {
        return {
          success: true,
          messageId: result.messages[0].id,
          status: result.messages[0].message_status,
          recipientId: result.contacts[0].wa_id,
          method: 'text'
        };
      } else {
        // If text message fails, try with hello_world template
        const templatePayload = {
          messaging_product: 'whatsapp',
          to: formattedNumber,
          type: 'template',
          template: {
            name: 'hello_world',
            language: {
              code: 'en_US'
            }
          }
        };

        const templateResponse = await fetch(`${this.baseUrl}/${this.phoneNumberId}/messages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(templatePayload)
        });

        const templateResult = await templateResponse.json();

        if (templateResponse.ok && templateResult.messages && templateResult.messages.length > 0) {
          return {
            success: true,
            messageId: templateResult.messages[0].id,
            status: templateResult.messages[0].message_status,
            recipientId: templateResult.contacts[0].wa_id,
            method: 'template_fallback',
            note: 'Text message failed, sent hello_world template instead',
            originalError: result.error?.message
          };
        } else {
          throw new Error(templateResult.error?.message || 'Both text and template send failed');
        }
      }
    } catch (error) {
      console.error('WhatsApp text send error:', error);

      if (this.isTokenExpiredError(error)) {
        return this.handleTokenExpiration(error);
      }

      return {
        success: false,
        error: error.message || 'Text message send failed',
        method: 'text'
      };
    }
  }

  // Send message to WhatsApp group
  async sendGroupMessage(groupId, message) {
    try {
      const payload = {
        messaging_product: 'whatsapp',
        recipient_type: 'group',
        to: groupId,
        type: 'text',
        text: {
          body: message
        }
      };

      const response = await fetch(`${this.baseUrl}/${this.phoneNumberId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.messages && result.messages.length > 0) {
        return {
          success: true,
          messageId: result.messages[0].id,
          status: result.messages[0].message_status,
          groupId: groupId,
          method: 'group_text'
        };
      } else {
        throw new Error(result.error?.message || `Group message failed: ${response.status}`);
      }
    } catch (error) {
      console.error('WhatsApp group send error:', error);

      if (this.isTokenExpiredError(error)) {
        return this.handleTokenExpiration(error);
      }

      return {
        success: false,
        error: error.message || 'Group message send failed',
        groupId: groupId
      };
    }
  }

  // Send template message to WhatsApp group
  async sendGroupTemplate(groupId, templateName = 'hello_world', languageCode = 'en_US', templateParams = []) {
    try {
      const payload = {
        messaging_product: 'whatsapp',
        recipient_type: 'group',
        to: groupId,
        type: 'template',
        template: {
          name: templateName,
          language: {
            code: languageCode
          }
        }
      };

      // Add template parameters if provided
      if (templateParams && templateParams.length > 0) {
        payload.template.components = [{
          type: 'body',
          parameters: templateParams
        }];
      }

      const response = await fetch(`${this.baseUrl}/${this.phoneNumberId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.messages && result.messages.length > 0) {
        return {
          success: true,
          messageId: result.messages[0].id,
          status: result.messages[0].message_status,
          groupId: groupId,
          templateName: templateName,
          method: 'group_template'
        };
      } else {
        throw new Error(result.error?.message || `Group template send failed: ${response.status}`);
      }
    } catch (error) {
      console.error('WhatsApp group template send error:', error);

      if (this.isTokenExpiredError(error)) {
        return this.handleTokenExpiration(error);
      }

      return {
        success: false,
        error: error.message || 'Group template send failed',
        groupId: groupId,
        templateName: templateName
      };
    }
  }

  // Send bulk template messages
  async sendBulkTemplate(recipients, templateName, templateParams = [], languageCode = 'en_US') {
    const results = [];

    for (const recipient of recipients) {
      const result = await this.sendTemplate(recipient, templateName, languageCode, templateParams);
      results.push({
        recipient: recipient,
        result: result
      });

      // Add small delay between messages to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return results;
  }

  // Helper method to format message for app display
  formatAppMessage(subject, body, senderName, organizationName) {
    return {
      subject: subject,
      preview: body.length > 100 ? body.substring(0, 100) + '...' : body,
      body: body,
      sender: senderName,
      organization: organizationName,
      timestamp: new Date().toISOString()
    };
  }

  // Template parameter formatters
  formatPaymentReminderTemplate(parentName, monthYear, amount, dueDate) {
    return [
      { type: 'text', text: parentName },
      { type: 'text', text: monthYear },
      { type: 'text', text: amount },
      { type: 'text', text: dueDate }
    ];
  }

  formatClassReminderTemplate(studentName, className, time) {
    return [
      { type: 'text', text: studentName },
      { type: 'text', text: className },
      { type: 'text', text: time }
    ];
  }

  formatWelcomeTemplate(studentName, firstClassDate, firstClassTime) {
    return [
      { type: 'text', text: studentName },
      { type: 'text', text: firstClassDate },
      { type: 'text', text: firstClassTime }
    ];
  }

  formatOverduePaymentNoticeTemplate(parentName, amount, monthYear, daysOverdue) {
    return [
      { type: 'text', text: parentName },
      { type: 'text', text: amount },
      { type: 'text', text: monthYear },
      { type: 'text', text: daysOverdue }
    ];
  }

  formatMonthlyBillNotificationTemplate(parentName, monthYear, amount, dueDate) {
    return [
      { type: 'text', text: parentName },
      { type: 'text', text: monthYear },
      { type: 'text', text: amount },
      { type: 'text', text: dueDate }
    ];
  }

  formatScheduleUpdateTemplate(className, oldTime, newTime, date) {
    return [
      { type: 'text', text: className },
      { type: 'text', text: oldTime },
      { type: 'text', text: newTime },
      { type: 'text', text: date }
    ];
  }

  // Template sending convenience methods
  async sendPaymentReminderTemplate(phoneNumber, parentName, monthYear, amount, dueDate) {
    const params = this.formatPaymentReminderTemplate(parentName, monthYear, amount, dueDate);
    return await this.sendTemplate(phoneNumber, 'payment_reminder_basic', 'en_US', params);
  }

  async sendClassReminderTemplate(phoneNumber, studentName, className, time) {
    const params = this.formatClassReminderTemplate(studentName, className, time);
    return await this.sendTemplate(phoneNumber, 'class_reminder_basic', 'en_US', params);
  }

  async sendOverduePaymentNoticeTemplate(phoneNumber, parentName, amount, monthYear, daysOverdue) {
    const params = this.formatOverduePaymentNoticeTemplate(parentName, amount, monthYear, daysOverdue);
    return await this.sendTemplate(phoneNumber, 'overdue_payment_notice', 'en_US', params);
  }

  async sendMonthlyBillNotificationTemplate(phoneNumber, parentName, monthYear, amount, dueDate) {
    const params = this.formatMonthlyBillNotificationTemplate(parentName, monthYear, amount, dueDate);
    return await this.sendTemplate(phoneNumber, 'monthly_bill_notification', 'en_US', params);
  }

  async sendScheduleUpdateTemplate(phoneNumber, className, oldTime, newTime, date) {
    const params = this.formatScheduleUpdateTemplate(className, oldTime, newTime, date);
    return await this.sendTemplate(phoneNumber, 'schedule_update', 'en_US', params);
  }

  async sendEventInviteTemplate(phoneNumber, userName, eventName, eventDate, eventTime) {
    const params = [
      { type: 'text', text: userName },
      { type: 'text', text: eventName },
      { type: 'text', text: eventDate },
      { type: 'text', text: eventTime }
    ];
    const result = await this.sendTemplate(phoneNumber, 'special_event_invite', 'en_US', params);

    if (!result.success) {
      // Marketing templates may fail outside 24-hour window, provide helpful message
      if (result.error && result.error.includes('24')) {
        result.note = 'Event invite failed - recipient may not have messaged within 24 hours. Marketing templates require recent conversation.';
      }
    }

    return result;
  }

  async sendNewClassScheduleTemplate(phoneNumber, userName, eventName, eventDate, eventTime, location) {
    // Use the new_class_schedule utility template with English (US) language code
    const params = [
      { type: 'text', text: userName },
      { type: 'text', text: eventName },
      { type: 'text', text: eventDate },
      { type: 'text', text: eventTime },
      { type: 'text', text: location }
    ];
    const result = await this.sendTemplate(phoneNumber, 'new_class_schedule', 'en_US', params);

    if (!result.success) {
      // Marketing templates may fail outside 24-hour window, provide helpful message
      if (result.error && result.error.includes('24')) {
        result.note = 'Special event notification failed - recipient may not have messaged within 24 hours. Marketing templates require recent conversation.';
      }
    }

    return result;
  }

  async sendGeneralMessageTemplate(phoneNumber, messageType, recipientName, subject, messageDetails) {
    // Use the general_message utility template (4 parameters: MessageType, Name, Subject, Body)
    // First parameter should be the message type from the compose message form
    const params = [
      { type: 'text', text: messageType },
      { type: 'text', text: recipientName },
      { type: 'text', text: subject },
      { type: 'text', text: messageDetails }
    ];
    const result = await this.sendTemplate(phoneNumber, 'general_message', 'en_US', params);

    if (!result.success) {
      // This is a utility template, so it should always work unless there's a config issue
      if (result.error && result.error.includes('24')) {
        result.note = 'General message failed unexpectedly - utility templates should not require 24-hour window.';
      }
    }

    return result;
  }

  async sendGeneralAnnouncement(phoneNumber, userName, topic, message, organizationName) {
    // general_announcement template only takes 3 parameters: [Name, Topic, Message]
    // Format parameters as JSON objects for WhatsApp API
    const params = [
      { type: "text", text: userName },
      { type: "text", text: topic },
      { type: "text", text: message }
    ];
    const result = await this.sendTemplate(phoneNumber, 'general_announcement', 'en_US', params);

    if (!result.success) {
      // Marketing templates may fail outside 24-hour window
      if (result.error && result.error.includes('24')) {
        result.note = 'Announcement failed - recipient may not have messaged within 24 hours. Marketing templates require recent conversation.';
      }
    }

    return result;
  }

  async sendGeneralMessage(phoneNumber, messageType, userName, subject, body) {
    // general_message template takes 4 parameters: [MessageType, Name, Subject, Body]
    // Format parameters as JSON objects for WhatsApp API
    const params = [
      { type: "text", text: messageType },
      { type: "text", text: userName },
      { type: "text", text: subject },
      { type: "text", text: body }
    ];
    const result = await this.sendTemplate(phoneNumber, 'general_message', 'en_US', params);

    if (!result.success) {
      // Check if it's a marketing template restriction
      if (result.error && result.error.includes('24')) {
        result.note = 'Message failed - recipient may not have messaged within 24 hours. Please ensure template is approved as utility template.';
      }
    }

    return result;
  }

  // Simple method to send any message - use utility template for better delivery
  async sendSimpleMessage(phoneNumber, userName, subject, body, organizationName = 'Martial Arts Club') {
    // Use class_reminder_basic (utility template) instead of general_announcement (marketing template)
    // This will actually deliver the message content without 24-hour restrictions
    return await this.sendClassReminderTemplate(phoneNumber, userName, subject, body);
  }

  // Template management helpers
  isMarketingTemplate(templateName) {
    const marketingTemplates = ['special_event_invite', 'new_class_schedule', 'general_announcement'];
    return marketingTemplates.includes(templateName);
  }

  getUtilityTemplates() {
    return [
      'hello_world',
      'payment_reminder_basic',
      'class_reminder_basic',
      'welcome_new_student',
      'schedule_update',
      'overdue_payment_notice',
      'monthly_bill_notification'
    ];
  }

  getMarketingTemplates() {
    return [
      'special_event_invite',
      'new_class_schedule',
      'general_announcement'
    ];
  }

  // Group messaging convenience methods
  async sendGroupEventInvite(groupId, eventName, eventDate, eventTime) {
    const params = [
      { type: 'text', text: eventName },
      { type: 'text', text: eventDate },
      { type: 'text', text: eventTime }
    ];
    return await this.sendGroupTemplate(groupId, 'special_event_invite', 'en_US', params);
  }

  async sendGroupAnnouncement(groupId, topic, message, organizationName) {
    const params = [
      { type: 'text', text: topic },
      { type: 'text', text: message },
      { type: 'text', text: organizationName }
    ];
    return await this.sendGroupTemplate(groupId, 'general_announcement', 'en_US', params);
  }

  async sendGroupGeneralMessage(groupId, messageType, recipientName, subject, body) {
    // general_message template for groups: [MessageType, RecipientName, Subject, Body]
    const params = [
      { type: 'text', text: messageType },
      { type: 'text', text: recipientName },
      { type: 'text', text: subject },
      { type: 'text', text: body }
    ];
    return await this.sendGroupTemplate(groupId, 'general_message', 'en_US', params);
  }

  async sendGroupClassReminder(groupId, className, time) {
    const params = [
      { type: 'text', text: className },
      { type: 'text', text: time }
    ];
    return await this.sendGroupTemplate(groupId, 'class_reminder_basic', 'en_US', params);
  }

  async sendGroupWelcomeMessage(groupId, studentName) {
    const params = [{ type: 'text', text: studentName }];
    return await this.sendGroupTemplate(groupId, 'welcome_new_student', 'en_US', params);
  }

  async sendGroupScheduleUpdate(groupId, className, oldTime, newTime, date) {
    const params = [
      { type: 'text', text: className },
      { type: 'text', text: oldTime },
      { type: 'text', text: newTime },
      { type: 'text', text: date }
    ];
    return await this.sendGroupTemplate(groupId, 'schedule_update', 'en_US', params);
  }

  formatPaymentReminder(parentName, monthYear, amount, centerName, organizationName, dueDate) {
    return `Dear ${parentName},

This is a friendly reminder that payment for ${monthYear} at ${centerName} is due.

Amount: $${amount}
Due Date: ${dueDate}

Please contact us if you have any questions.

Thank you,
${organizationName}`;
  }

  // Test configuration
  async testConfiguration() {
    try {
      const accountInfo = await this.validateToken();

      if (accountInfo.success) {
        return {
          success: true,
          message: 'WhatsApp configuration is valid',
          accountInfo: {
            phoneNumber: accountInfo.phoneNumber,
            verifiedName: accountInfo.verifiedName,
            qualityRating: accountInfo.qualityRating
          }
        };
      } else {
        return {
          success: false,
          error: accountInfo.error,
          message: 'WhatsApp configuration failed validation'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to test WhatsApp configuration'
      };
    }
  }


  async getGroups() {
    try {
      if (!this.accessToken || !this.phoneNumberId) {
        throw new Error('Missing access token or phone number ID');
      }

      // Note: WhatsApp Business API doesn't provide a direct endpoint to list groups
      // Groups are typically discovered through webhook events or known group IDs
      return {
        success: false,
        error: 'WhatsApp Business API does not provide group listing endpoint',
        note: 'Groups must be discovered through webhooks or manually provided',
        instructions: [
          '1. Set up webhooks to capture group interactions',
          '2. Monitor webhook events for group messages',
          '3. Extract group IDs from incoming webhook data',
          '4. Manually obtain group IDs from group admins'
        ]
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        message: 'Failed to get groups information'
      };
    }
  }


  async getGroupInfo(groupId) {
    try {
      if (!this.accessToken || !groupId) {
        throw new Error('Missing access token or group ID');
      }

      // Try to get group information by sending a test message (this will fail but may provide group info)
      const response = await fetch(`${this.baseUrl}/${this.phoneNumberId}/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          messaging_product: 'whatsapp',
          recipient_type: 'group',
          to: groupId,
          type: 'text',
          text: {
            body: 'Test message to verify group access'
          }
        })
      });

      const result = await response.json();

      if (response.ok && result.messages) {
        return {
          success: true,
          groupId: groupId,
          status: 'accessible',
          messageId: result.messages[0]?.id,
          note: 'Group is accessible and bot can send messages'
        };
      } else {
        return {
          success: false,
          groupId: groupId,
          error: result.error?.message || 'Group not accessible',
          status: 'inaccessible',
          note: 'Bot may not be added to this group or group ID is invalid'
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message,
        groupId: groupId,
        status: 'error'
      };
    }
  }

  // Send a discovery message to help identify group ID
  async sendGroupDiscoveryMessage(groupId, customMessage = null) {
    const message = customMessage || `🤖 WhatsApp Bot Discovery Test

This message confirms:
✅ Group ID: ${groupId}
✅ Bot has access to this group
✅ Messages can be sent successfully

This is an automated test message for group configuration.`;

    return await this.sendGroupMessage(groupId, message);
  }

  // Helper method to validate group ID format
  isValidGroupId(groupId) {
    if (!groupId || typeof groupId !== 'string') {
      return false;
    }

    // WhatsApp group IDs follow the format: number@g.us
    const groupIdPattern = /^\d+@g\.us$/;
    return groupIdPattern.test(groupId);
  }


  getGroupIdInstructions() {
    return {
      methods: [
        {
          method: 'Webhook Events',
          description: 'Set up webhooks to capture group messages and extract group IDs',
          difficulty: 'Technical',
          steps: [
            'Configure webhook endpoint in Meta Business Manager',
            'Subscribe to message events',
            'Monitor incoming webhook data for group interactions',
            'Extract group ID from webhook payload'
          ]
        },
        {
          method: 'WhatsApp Web Inspector',
          description: 'Use browser developer tools to find group IDs',
          difficulty: 'Advanced',
          steps: [
            'Open WhatsApp Web in Chrome/Firefox',
            'Open Developer Tools (F12)',
            'Navigate to the group chat',
            'Check Network tab for API calls containing group ID',
            'Look for IDs in format: number@g.us'
          ]
        },
        {
          method: 'Third-party Tools',
          description: 'Use WhatsApp group management tools',
          difficulty: 'Easy',
          steps: [
            'Use tools like "WhatsApp Group Link Generator"',
            'Extract group ID from generated links',
            'Convert invite links to group IDs using online converters'
          ]
        },
        {
          method: 'Bot Integration',
          description: 'Add bot to group and capture ID from first message',
          difficulty: 'Medium',
          steps: [
            'Add your WhatsApp Business number to the group',
            'Send a test message from the group',
            'Check webhook logs for the group ID',
            'Group ID will be in the "from" field of group messages'
          ]
        }
      ],
      tips: [
        'Group IDs always end with @g.us',
        'Group IDs are long numbers (typically 15+ digits)',
        'You need admin permissions to add bots to groups',
        'Test group access after obtaining the ID'
      ]
    };
  }
}

export default WhatsAppService;